import { PropsWithChildren, createContext, useContext, useMemo } from "react";

import NewFeaturesToggleAPI from "src/api/new-interfaces/live/features-toggle";
import { FEATURES_TOGGLE_KEYS } from "src/consts/features";
import { useState } from "src/hooks/state";
import { FREQUENCY, REQUEST_TYPE, useSWR } from "src/hooks/swr";
import { useUserInfo } from "src/hooks/user";
type FeaturesToggleTypes = {
  [key in keyof typeof FEATURES_TOGGLE_KEYS]: boolean;
};

export const FeaturesToggleContext = createContext<FeaturesToggleTypes>(undefined);
export const LoadingFeaturesToggleContext = createContext<boolean>(true);

export default function FeaturesToggleProvider({ children }: PropsWithChildren<{}>) {
  const { user } = useUserInfo();
  const featuresToggleListPath = NewFeaturesToggleAPI.listFeatures;
  const [features, setFeatures] = useState<FeaturesToggleTypes>(
    (() => {
      const newFeaturesValue = {} as FeaturesToggleTypes;
      Object.keys(FEATURES_TOGGLE_KEYS).map((key) => {
        newFeaturesValue[key as keyof FeaturesToggleTypes] = false;
      });
      return newFeaturesValue as FeaturesToggleTypes;
    })()
  );

  const urls = useMemo(() => {
    return (
      Object.values(FEATURES_TOGGLE_KEYS).map((key) => {
        const urlSearchParams = new URLSearchParams({
          key
        }).toString();
        return `${featuresToggleListPath}?${urlSearchParams}`;
      }) || []
    );
  }, [featuresToggleListPath]);

  const { loading } = useSWR({
    initialData: [],
    endpoint: user?.company?.id && urls,
    type: REQUEST_TYPE.GATEWAY,
    frequency: FREQUENCY.RARE,
    laggy: true,
    callback: ({ data }) => {
      const newFeaturesValue = {} as FeaturesToggleTypes;
      Object.keys(FEATURES_TOGGLE_KEYS).map((key, index) => {
        newFeaturesValue[key as keyof FeaturesToggleTypes] = data[index]?.value;
      });
      setFeatures(newFeaturesValue);
    }
  });

  return (
    <FeaturesToggleContext.Provider value={features}>
      <LoadingFeaturesToggleContext.Provider value={loading}>{children}</LoadingFeaturesToggleContext.Provider>
    </FeaturesToggleContext.Provider>
  );
}

export function useFeatureToggle(key: keyof typeof FEATURES_TOGGLE_KEYS) {
  const features = useContext(FeaturesToggleContext);
  return features[key];
}
export function useIsLoadingFeatureToggle() {
  return useContext(LoadingFeaturesToggleContext);
}
